import { useEffect, useState } from 'react'
import { Controller, useFormContext } from '@astro/forms'

function useObjectUrl(file) {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    if (!file) {
      setUrl(null)
      return undefined
    }
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])
  return url
}

/** Avatar + copy + action pill in one clickable row (replaces the wide drop zone). */
export default function ProfilePhotoField({ name }) {
  const { control } = useFormContext()
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (file) => {
          if (!file) return 'A profile photo is required.'
          // Upstream rejects non-images — catch it on this step, not at submit.
          if (!/^image\/(jpeg|png)$/.test(file.type)) return 'Photo must be a JPG or PNG image.'
          if (file.size > 1024 * 1024 * 5) return 'Photo must be under 5MB.'
          return true
        },
      }}
      render={({ field, fieldState }) => <PhotoRow field={field} error={fieldState.error} />}
    />
  )
}

function PhotoRow({ field, error }) {
  const previewUrl = useObjectUrl(field.value)
  return (
    <div>
      <span className="ar-label">
        Profile Photo <span className="normal-case font-normal text-stone-400">(JPG/PNG, max 5MB)</span>
      </span>
      <label
        className={`flex items-center gap-4 rounded-2xl border-[1.5px] p-4 cursor-pointer transition-all bg-[#FAFAF9] hover:bg-[#FFF8EE] ${
          field.value ? 'border-green-500/45' : 'border-goldAccent/20 hover:border-goldPrimary/60'
        }`}
      >
        <input
          type="file"
          accept=".jpg,.jpeg,.png"
          className="hidden"
          onChange={(e) => field.onChange(e.target.files?.[0] ?? null)}
        />
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Profile preview"
            className="w-14 h-14 rounded-full object-cover border-2 border-goldPrimary/40 shadow-md shrink-0"
          />
        ) : (
          <span className="w-14 h-14 rounded-full border-2 border-dashed border-goldAccent/35 bg-goldPrimary/5 text-goldAccent flex items-center justify-center text-lg shrink-0">
            📷
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-astroDark truncate">
            {field.value ? field.value.name : 'Upload your profile photo'}
          </p>
          <p className="text-[11px] text-stone-400 mt-0.5 leading-relaxed">
            A clear, front-facing photo works best — this is what seekers will see.
          </p>
        </div>
        <span className="hidden sm:inline-flex items-center border border-goldAccent/40 text-goldAccent font-bold text-[10.5px] tracking-wider uppercase px-4 py-1.5 rounded-full whitespace-nowrap">
          {field.value ? 'Change' : 'Choose Photo'}
        </span>
      </label>
      {error && <p className="ar-error">{error.message}</p>}
    </div>
  )
}
